import { motion } from "framer-motion";
import { ChevronDown, Eye } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = '/api';

export default function HeroSection() {
  const [visitorCount, setVisitorCount] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    // Only increment once per browser session, not on every page load
    const alreadyCounted = sessionStorage.getItem("tiina_visitor_counted");
    if (!alreadyCounted) {
      axios.post(`${API}/visitor-count`).then(r => {
        setVisitorCount(r.data.count);
        sessionStorage.setItem("tiina_visitor_counted", "1");
      }).catch(() => {
        axios.get(`${API}/visitor-count`).then(r => setVisitorCount(r.data.count)).catch(() => {});
      });
    } else {
      // Already counted this session, just read the current count
      axios.get(`${API}/visitor-count`).then(r => setVisitorCount(r.data.count)).catch(() => {});
    }
  }, []);

  // Load video as blob to bypass proxy no-cache headers that prevent buffering
  useEffect(() => {
    let blobUrl = null;
    fetch("/api/video/hero")
      .then(r => r.blob())
      .then(blob => {
        blobUrl = URL.createObjectURL(blob);
        setVideoUrl(blobUrl);
      })
      .catch(() => {});
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, []);

  // Once blob URL is set and video element exists, play it
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    video.muted = true;
    video.play().catch(() => {});
  }, [videoUrl]);

  // Pause/play on scroll visibility
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Static aerial Auckland background */}
      <img
        src="/hero-auckland-aerial.jpg"
        alt="Auckland aerial"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-bg"
      />

      {/* Subtle dark overlay for text readability - no colour filter */}
      <div className="absolute inset-0 bg-black/40 z-[1]" />

      {/* Content - vertically centred */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center flex flex-col items-center justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-4 sm:mb-6 flex justify-center items-center"
        >
          <img src="/tiina-logo-hero-white.png" alt="T.I.I.N.A. The Indigenous Innovation Network Agency" className="w-[160px] sm:w-[220px] lg:w-[280px] drop-shadow-2xl" data-testid="hero-logo" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="font-['Syne'] text-xl sm:text-2xl lg:text-4xl font-bold tracking-wide text-white mb-6 sm:mb-8"
        >
          The Indigenous Innovation Network Agency
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-base sm:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed font-light"
        >
          Experienced directors, researchers, strategists, and practitioners who operate at the intersection of culture, commerce, and community. We do not adapt who we are to fit the room. We change the room.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="mt-8 flex flex-wrap justify-center gap-4"
        >
          <a
            href="#about"
            data-testid="hero-explore-btn"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 text-white font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-all duration-200 hover:scale-105"
            style={{ background: "linear-gradient(135deg, #bb321a, #364225)" }}
          >
            Explore Our Story
          </a>
          <a
            href="#contact"
            data-testid="hero-connect-btn"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white font-bold text-sm uppercase tracking-wider hover:bg-white/10 transition-all duration-200"
          >
            Connect With Us
          </a>
        </motion.div>
      </div>

      {/* Visitor counter */}
      {visitorCount && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-8 sm:left-12 flex items-center gap-3 text-white/40 z-10"
          data-testid="visitor-counter"
        >
          <Eye className="w-6 h-6" />
          <span className="text-lg font-semibold tracking-wider">{visitorCount.toLocaleString()}</span>
        </motion.div>
      )}

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-white/60" />
        </motion.div>
      </motion.div>
    </section>
  );
}
